import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { PrivateLayout } from '@/layouts/PrivateLayout';
import { TeacherLayout } from '@/layouts/TeacherLayout';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ActivateAccountPage } from '@/pages/ActivateAccountPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { TimetableDesignerPage } from '@/pages/TimetableDesignerPage';
import { MasterDataPage } from '@/pages/MasterDataPage';
import { AbsencesPage } from '@/pages/AbsencesPage';
import { SubstitutionsPage } from '@/pages/SubstitutionsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { ImportExportPage } from '@/pages/ImportExportPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { SchoolManagementPage } from '@/pages/SchoolManagementPage';
import { UserManagementPage } from '@/pages/UserManagementPage';
import { TeacherDashboardPage } from '@/pages/teacher/TeacherDashboardPage';
import { TeacherTimetablePage } from '@/pages/teacher/TeacherTimetablePage';
import { TeacherAbsencesPage } from '@/pages/teacher/TeacherAbsencesPage';
import { TeacherSettingsPage } from '@/pages/teacher/TeacherSettingsPage';
import { ProtectedRoute } from '@/components/ProtectedRoute';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'signup', element: <SignupPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'activate-account', element: <ActivateAccountPage /> },
    ],
  },
  {
    path: '/teacher',
    element: (
      <ProtectedRoute requiredRoles={['teacher']}>
        <TeacherLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <TeacherDashboardPage /> },
      { path: 'timetable', element: <TeacherTimetablePage /> },
      { path: 'absences', element: <TeacherAbsencesPage /> },
      { path: 'settings', element: <TeacherSettingsPage /> },
    ],
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <PrivateLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/app/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'timetable',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'standard']}>
            <TimetableDesignerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'master-data',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <MasterDataPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'absences',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'standard']}>
            <AbsencesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'substitutions',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'standard']}>
            <SubstitutionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'standard']}>
            <ReportsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'import-export',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'standard']}>
            <ImportExportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '/super-admin',
    element: (
      <ProtectedRoute requiredRoles={['super_admin']}>
        <PrivateLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/super-admin/schools" replace />,
      },
      {
        path: 'schools',
        element: (
          <ProtectedRoute requiredRoles={['super_admin']}>
            <SchoolManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'users',
        element: (
          <ProtectedRoute requiredRoles={['super_admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
